import type { Charge, ChargeState, Money, MoneyCurrency } from '../../api/generated/model';
import { formatNumber } from '../../i18n/datetime';

/**
 * Formata um `Money` para exibicao.
 *
 * O contrato guarda dinheiro em **centavos inteiros** (`amountCents`), e a
 * unica conversao para fracao acontece aqui, no ultimo passo antes da tela.
 * Nenhum outro lugar da SPA divide, soma ou compara valores em ponto
 * flutuante: `0.1 + 0.2` nao e `0.3`, e um centavo perdido numa fatura e um
 * centavo que o aluno vai cobrar da escola.
 *
 * `Intl.NumberFormat` com `minimumFractionDigits: 2` recebe o valor ja
 * dividido, mas a divisao por 100 e exata em binario para qualquer inteiro
 * seguro — o erro so apareceria se somassemos fracoes, que e justamente o que
 * `sumCents` evita.
 */
export function formatMoney(money: Money, locale: string): string {
  return formatNumber(money.amountCents / 100, locale, {
    style: 'currency',
    currency: money.currency,
    minimumFractionDigits: 2,
  });
}

/**
 * Soma valores **em centavos**, nunca em fracao.
 *
 * Existe para que nenhuma tela seja tentada a somar `formatMoney` de volta ou
 * a acumular `amountCents / 100`. Recusa moedas diferentes em vez de somar
 * numeros que nao se somam.
 */
export function sumCents(values: readonly Money[]): number {
  const currencies = new Set(values.map((value) => value.currency));
  if (currencies.size > 1) {
    throw new Error('soma de valores em moedas diferentes');
  }
  return values.reduce((total, value) => total + value.amountCents, 0);
}

/** Fatura que ainda espera dinheiro do aluno: e dela que sai "Em aberto". */
const AWAITING_PAYMENT: ReadonlySet<ChargeState> = new Set<ChargeState>(['pending', 'overdue']);

/** Dinheiro que ja saiu do aluno e espera a confirmacao do provedor. */
const IN_PROCESSING: ReadonlySet<ChargeState> = new Set<ChargeState>(['processing']);

/**
 * Faturas fora do ciclo de cobranca. So o historico as mostra: no que esta em
 * cobranca elas nao pedem nada de ninguem.
 */
const SETTLED: ReadonlySet<ChargeState> = new Set<ChargeState>(['paid', 'cancelled', 'refunded']);

export function isSettled(state: ChargeState): boolean {
  return SETTLED.has(state);
}

/**
 * Os dois totais do resumo, **separados por estado**.
 *
 * Um so numero misturava os dois: `outstanding`, no contrato, soma `pending`,
 * `processing` e `overdue` na mesma conta (ver `SumOutstandingByPerson`), e a
 * tela o rotulava "Em aberto" — o mesmo rotulo que a fatura `pending` carrega
 * logo abaixo. O aluno com uma unica fatura em aberto de R$ 120,00 lia
 * "Em aberto R$ 389,00" e concluia que devia os R$ 389, quando R$ 269 ja tinham
 * saido da conta dele e esperavam o banco.
 *
 * Os dois totais nascem da lista de faturas do resumo, e nao de `outstanding`,
 * porque um total precisa poder ser conferido somando o que esta escrito na
 * tela. A alternativa recusada foi manter `outstanding` com um rotulo novo: o
 * numero continuaria sem nenhum conjunto de faturas visiveis que o explicasse.
 *
 * `draft` e `under_review` ficam de fora dos dois, como no servidor: uma nao
 * foi emitida e a outra esta sob disputa, e somar qualquer das duas seria
 * cobrar do aluno um valor que ninguem afirmou que ele deve.
 */
export interface OpenTotals {
  readonly awaitingPayment: Money;
  readonly inProcessing: Money;
}

export function openTotals(charges: readonly Charge[], currency: MoneyCurrency): OpenTotals {
  const totalOf = (states: ReadonlySet<ChargeState>): Money => ({
    amountCents: sumCents(charges.filter((c) => states.has(c.state)).map((c) => c.amount)),
    currency,
  });

  return { awaitingPayment: totalOf(AWAITING_PAYMENT), inProcessing: totalOf(IN_PROCESSING) };
}
